import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
  Modal,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useThemeMode } from "../../theme/ThemeContext";
import Button from "../../components/Button";
import { TitleText } from "./onboarding_styles";
import { llmOnboardingEnabled } from "./llm/featureGate";
import {
  updateDoc,
  doc,
  collection,
  addDoc,
  writeBatch,
} from "firebase/firestore";
import { tracedCallable } from "../../utilities/tracedCallable";
import { useAuth } from "../../services/use-auth";
import { CenteringDiv } from "../../assets/containers";
import { RowWrapper } from "../../assets/containers";
import {
  defaultAccounts,
  defaultAccountsHierarchy,
  defaultFunds,
  defaultFundsHierarchy,
  defaultContacts,
} from "./defaultsForExpert";
import SignOutModal from "../../nav/signOut";
import { Info } from "@mui/icons-material";
import { tierDisplayName } from "../../config/entitlements";

// Shared by AddOrg and AddOrgForm auto-assign snackbars
const autoAssignMessage = (level) =>
  `This organization has been automatically added to your ${tierDisplayName(level)} subscription.`;

const GOOGLE_ADS_CONVERSION_ID = "AW-17960007423/I1pyCNqyyfsbEP_t__NC";

/** Fire Google Ads free-trial conversion (production only). */
function trackFreeTrialConversion() {
  if (window.location.hostname !== "app.t3books.com") return;
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", {
      send_to: GOOGLE_ADS_CONVERSION_ID,
    });
  }
}

export const AddOrg = ({
  db,
  userData,
  user,
  closeModal,
  inviteCheckState,
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [autoAssignSnackbar, setAutoAssignSnackbar] = useState({
    open: false,
    message: "",
    subscriptionLevel: null,
  });
  const authHook = useAuth();

  // Simplified validation schema - only essential fields
  const validationSchema = Yup.object({
    orgName: Yup.string()
      .max(50, "Must be 50 characters or less")
      .required("Your organization needs a name"),
    orgType: Yup.string()
      .required("Required for proper setup")
      .transform((value) => (value === "" ? null : value)),
    financialYearStart: Yup.string().transform((value) =>
      value === "" ? null : value,
    ),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      orgName: "",
      orgType: "",
      financialYearStart: "",
    },
  });

  // Custom TextField component with theme-aware focus colors
  const ThemedTextField = ({
    name,
    label,
    required = false,
    select = false,
    children,
    ...props
  }) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            {...props}
            label={label}
            required={required}
            select={select}
            fullWidth
            error={!!errors[name]}
            helperText={errors[name]?.message}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                "&.Mui-focused": {
                  color:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                },
              },
            }}>
            {children}
          </TextField>
        )}
      />
    );
  };

  const onSubmit = async (values) => {
    setIsCreating(true);

    try {
      const setDefaultSetup = async (db, orgID, retryCount = 3) => {
        let attempts = 0;
        let success = false;

        while (attempts < retryCount && !success) {
          try {
            const batch = writeBatch(db);

            const orgRef = doc(db, "orgs", orgID);
            const accountsHierarchyRef = doc(
              db,
              "orgs",
              orgID,
              "organizationAndSettings",
              "accountsHierarchy",
            );
            const fundsHierarchyRef = doc(
              db,
              "orgs",
              orgID,
              "organizationAndSettings",
              "fundsHierarchy",
            );

            batch.set(accountsHierarchyRef, defaultAccountsHierarchy);
            batch.set(fundsHierarchyRef, defaultFundsHierarchy);

            defaultAccounts.forEach((account) => {
              const accountRef = doc(db, "orgs", orgID, "accounts", account.id);
              batch.set(accountRef, account);
            });

            defaultFunds.forEach((fund) => {
              const fundRef = doc(db, "orgs", orgID, "funds", fund.id);
              batch.set(fundRef, fund);
            });

            defaultContacts.forEach((contact) => {
              const contactRef = doc(db, "orgs", orgID, "contacts", contact.id);
              batch.set(contactRef, contact);
            });

            batch.update(orgRef, { onboarded: true }, { merge: true });

            await batch.commit();
            success = true;
          } catch (error) {
            attempts++;
            console.error(`Attempt ${attempts} failed:`, error);
            if (attempts >= retryCount) {
              throw new Error(`Failed after ${attempts} attempts`);
            }
          }
        }
      };

      const addOrg = async (values) => {
        const today = new Date();
        const newOrgData = {
          ...values,
          users: {
            [user.uid]: {
              roles: ["owner", "subscriber", "admin"],
              username: userData.username,
              email: userData.email,
            },
          },
          onboarded: false,
          creationDate: today,
        };
        const orgs = userData.orgs ? userData.orgs : [];
        console.log("Adding org: ", newOrgData);
        const orgsRef = collection(db, "orgs");
        const docRef = await addDoc(orgsRef, newOrgData);
        const orgID = docRef.id;
        console.log("Added org: ", orgID);

        // Set up initial subscription using cloud function
        const setupInitialSubscription = tracedCallable(
          authHook.functions,
          "setupInitialSubscription",
        );

        try {
          const subscriptionResult = await setupInitialSubscription({
            orgId: orgID,
          });
          console.log("Subscription setup result:", subscriptionResult.data);

          // Check if org was auto-assigned to multi-subscription
          if (subscriptionResult.data?.autoAssigned) {
            setAutoAssignSnackbar({
              open: true,
              message: autoAssignMessage(subscriptionResult.data.subscriptionLevel),
              subscriptionLevel: subscriptionResult.data.subscriptionLevel,
            });
          }
        } catch (error) {
          console.error("Error setting up subscription:", error);
          // Continue with org setup even if subscription setup fails
          // The user can set up subscription later
        }

        orgs.push({
          ...values,
          orgID: orgID,
        });
        console.log("Orgs: ", orgs);
        // LLM onboarding (dev/qa) builds the real chart via the chat, so skip the default seed +
        // auto-onboard here — the org stays onboarded:false and routes to /onboarding/chat.
        if (!llmOnboardingEnabled()) await setDefaultSetup(db, orgID);
        await updateDoc(
          doc(db, "users", user.uid),
          {
            orgs: orgs,
            lastOrgUsed: orgID,
          },
          { merge: true },
        );
        trackFreeTrialConversion();
        if (closeModal) closeModal();
      };

      await addOrg(values);
    } catch (error) {
      console.error("Error creating organization:", error);
      alert("Failed to create organization. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const formContent = (
    <>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="onboarding-addorg-form">
        <CenteringDiv>
          <TitleText
            style={{
              color: theme.palette.text.primary,
              fontSize: "1.5rem",
            }}>
            Tell us about your org
          </TitleText>
          <div style={{ height: "24px" }} />

          <Grid container spacing={3} sx={{ width: "100%" }}>
            <Grid size={{ xs: 12 }}>
              <ThemedTextField
                name="orgName"
                label="Organization Name"
                required
                data-testid="onboarding-addorg-input-name"
              />
            </Grid>

            <Grid size={{ xs: 12 }} sm={6}>
              <ThemedTextField
                name="orgType"
                label="Organization Type"
                required
                select
                data-testid="onboarding-addorg-select-type">
                <MenuItem value="church">Church</MenuItem>
                <MenuItem value="generalNP">General Nonprofit</MenuItem>
              </ThemedTextField>
            </Grid>

            <Grid size={{ xs: 12 }} sm={6}>
              <ThemedTextField
                name="financialYearStart"
                label="Financial Year Start (Optional)"
                select
                data-testid="onboarding-addorg-select-year-start">
                <MenuItem value="">Select later</MenuItem>
                <MenuItem value="jan">January</MenuItem>
                <MenuItem value="feb">February</MenuItem>
                <MenuItem value="mar">March</MenuItem>
                <MenuItem value="apr">April</MenuItem>
                <MenuItem value="may">May</MenuItem>
                <MenuItem value="jun">June</MenuItem>
                <MenuItem value="jul">July</MenuItem>
                <MenuItem value="aug">August</MenuItem>
                <MenuItem value="sep">September</MenuItem>
                <MenuItem value="oct">October</MenuItem>
                <MenuItem value="nov">November</MenuItem>
                <MenuItem value="dec">December</MenuItem>
              </ThemedTextField>
            </Grid>
          </Grid>

          <div style={{ height: "32px" }} />

          {/* Invite checking status */}
          {inviteCheckState?.checking && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={20}
                  sx={{
                    color:
                      mode === "light"
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                  }}
                />
                <TitleText
                  style={{
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    margin: 0,
                  }}>
                  Checking for invites to other orgs...
                </TitleText>
                <Info
                  sx={{
                    fontSize: "18px",
                    color: theme.palette.text.secondary,
                    cursor: "help",
                  }}
                  title="If someone has invited you to their organization, we'll automatically add you to it instead of creating a new one."
                />
              </Box>
            </Box>
          )}

          {inviteCheckState?.waiting && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.success.light,
                borderRadius: 1,
                border: `1px solid ${theme.palette.success.main}`,
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={20}
                  sx={{ color: theme.palette.success.main }}
                />
                <TitleText
                  style={{
                    fontSize: "16px",
                    color: theme.palette.success.dark,
                    margin: 0,
                  }}>
                  Organization found! Adding you...
                </TitleText>
              </Box>
            </Box>
          )}

          {isCreating ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}>
              <CircularProgress
                sx={{
                  color:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                }}
              />
              <TitleText
                style={{
                  fontSize: "16px",
                  color: theme.palette.text.primary,
                }}>
                Creating organization and setting up subscription...
              </TitleText>
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                type="submit"
                disabled={
                  isCreating ||
                  inviteCheckState?.checking ||
                  inviteCheckState?.waiting
                }
                data-testid="onboarding-addorg-button-submit">
                Create Organization
              </Button>
              <div style={{ height: "16px" }} />
              <p
                style={{
                  color: theme.palette.text.secondary,
                  fontFamily: "MontserratMed",
                  textAlign: "center",
                  margin: 0,
                }}>
                Or ask the admin of an existing organization to add you.
              </p>
            </>
          )}
        </CenteringDiv>
      </form>

      {/* Auto-assignment notification snackbar */}
      <Snackbar
        open={autoAssignSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setAutoAssignSnackbar({ ...autoAssignSnackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAutoAssignSnackbar({ ...autoAssignSnackbar, open: false })}
          severity="success"
          sx={{ width: "100%" }}
        >
          {autoAssignSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        padding: 4,
        maxWidth: 600,
        width: "90%",
        maxHeight: "90vh",
        overflow: "auto",
      }}>
      {formContent}
    </Paper>
  );
};

// Exported form component without Paper wrapper for embedding in other modals
export const AddOrgForm = ({
  db,
  userData,
  user,
  closeModal,
  inviteCheckState,
}) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  const [isCreating, setIsCreating] = useState(false);
  const [autoAssignSnackbar, setAutoAssignSnackbar] = useState({
    open: false,
    message: "",
    subscriptionLevel: null,
  });
  const authHook = useAuth();

  const validationSchema = Yup.object({
    orgName: Yup.string()
      .max(50, "Must be 50 characters or less")
      .required("Your organization needs a name"),
    orgType: Yup.string()
      .required("Required for proper setup")
      .transform((value) => (value === "" ? null : value)),
    financialYearStart: Yup.string().transform((value) =>
      value === "" ? null : value,
    ),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      orgName: "",
      orgType: "",
      financialYearStart: "",
    },
  });

  const ThemedTextField = ({
    name,
    label,
    required = false,
    select = false,
    children,
    ...props
  }) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            {...props}
            label={label}
            required={required}
            select={select}
            fullWidth
            error={!!errors[name]}
            helperText={errors[name]?.message}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                "&.Mui-focused": {
                  color:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                },
              },
            }}>
            {children}
          </TextField>
        )}
      />
    );
  };

  const onSubmit = async (values) => {
    setIsCreating(true);

    try {
      const setDefaultSetup = async (db, orgID, retryCount = 3) => {
        let attempts = 0;
        let success = false;

        while (attempts < retryCount && !success) {
          try {
            const batch = writeBatch(db);

            const orgRef = doc(db, "orgs", orgID);
            const accountsHierarchyRef = doc(
              db,
              "orgs",
              orgID,
              "organizationAndSettings",
              "accountsHierarchy",
            );
            const fundsHierarchyRef = doc(
              db,
              "orgs",
              orgID,
              "organizationAndSettings",
              "fundsHierarchy",
            );

            batch.set(accountsHierarchyRef, defaultAccountsHierarchy);
            batch.set(fundsHierarchyRef, defaultFundsHierarchy);

            defaultAccounts.forEach((account) => {
              const accountRef = doc(db, "orgs", orgID, "accounts", account.id);
              batch.set(accountRef, account);
            });

            defaultFunds.forEach((fund) => {
              const fundRef = doc(db, "orgs", orgID, "funds", fund.id);
              batch.set(fundRef, fund);
            });

            defaultContacts.forEach((contact) => {
              const contactRef = doc(db, "orgs", orgID, "contacts", contact.id);
              batch.set(contactRef, contact);
            });

            batch.update(orgRef, { onboarded: true }, { merge: true });

            await batch.commit();
            success = true;
          } catch (error) {
            attempts++;
            console.error(`Attempt ${attempts} failed:`, error);
            if (attempts >= retryCount) {
              throw new Error(`Failed after ${attempts} attempts`);
            }
          }
        }
      };

      const addOrg = async (values) => {
        const today = new Date();
        const newOrgData = {
          ...values,
          users: {
            [user.uid]: {
              roles: ["owner", "subscriber", "admin"],
              username: userData.username,
              email: userData.email,
            },
          },
          onboarded: false,
          creationDate: today,
        };
        const orgs = userData.orgs ? userData.orgs : [];
        console.log("Adding org: ", newOrgData);
        const orgsRef = collection(db, "orgs");
        const docRef = await addDoc(orgsRef, newOrgData);
        const orgID = docRef.id;
        console.log("Added org: ", orgID);

        const setupInitialSubscription = tracedCallable(
          authHook.functions,
          "setupInitialSubscription",
        );

        try {
          const subscriptionResult = await setupInitialSubscription({
            orgId: orgID,
          });
          console.log("Subscription setup result:", subscriptionResult.data);

          if (subscriptionResult.data?.autoAssigned) {
            setAutoAssignSnackbar({
              open: true,
              message: autoAssignMessage(subscriptionResult.data.subscriptionLevel),
              subscriptionLevel: subscriptionResult.data.subscriptionLevel,
            });
          }
        } catch (error) {
          console.error("Error setting up subscription:", error);
        }

        orgs.push({
          ...values,
          orgID: orgID,
        });
        console.log("Orgs: ", orgs);
        // LLM onboarding (dev/qa) builds the real chart via the chat, so skip the default seed +
        // auto-onboard here — the org stays onboarded:false and routes to /onboarding/chat.
        if (!llmOnboardingEnabled()) await setDefaultSetup(db, orgID);
        await updateDoc(
          doc(db, "users", user.uid),
          {
            orgs: orgs,
            lastOrgUsed: orgID,
          },
          { merge: true },
        );
        trackFreeTrialConversion();
        if (closeModal) closeModal();
      };

      await addOrg(values);
    } catch (error) {
      console.error("Error creating organization:", error);
      alert("Failed to create organization. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="onboarding-addorg-embed-form">
        <CenteringDiv>
          <TitleText
            style={{
              color: theme.palette.text.primary,
              fontSize: "1.5rem",
            }}>
            Tell us about your org
          </TitleText>
          <div style={{ height: "24px" }} />

          <Grid container spacing={3} sx={{ width: "100%" }}>
            <Grid size={{ xs: 12 }}>
              <ThemedTextField
                name="orgName"
                label="Organization Name"
                required
                data-testid="onboarding-addorg-embed-input-name"
              />
            </Grid>

            <Grid size={{ xs: 12 }} sm={6}>
              <ThemedTextField
                name="orgType"
                label="Organization Type"
                required
                select
                data-testid="onboarding-addorg-embed-select-type">
                <MenuItem value="church">Church</MenuItem>
                <MenuItem value="generalNP">General Nonprofit</MenuItem>
              </ThemedTextField>
            </Grid>

            <Grid size={{ xs: 12 }} sm={6}>
              <ThemedTextField
                name="financialYearStart"
                label="Financial Year Start (Optional)"
                select
                data-testid="onboarding-addorg-embed-select-year-start">
                <MenuItem value="">Select later</MenuItem>
                <MenuItem value="jan">January</MenuItem>
                <MenuItem value="feb">February</MenuItem>
                <MenuItem value="mar">March</MenuItem>
                <MenuItem value="apr">April</MenuItem>
                <MenuItem value="may">May</MenuItem>
                <MenuItem value="jun">June</MenuItem>
                <MenuItem value="jul">July</MenuItem>
                <MenuItem value="aug">August</MenuItem>
                <MenuItem value="sep">September</MenuItem>
                <MenuItem value="oct">October</MenuItem>
                <MenuItem value="nov">November</MenuItem>
                <MenuItem value="dec">December</MenuItem>
              </ThemedTextField>
            </Grid>
          </Grid>

          <div style={{ height: "32px" }} />

          {inviteCheckState?.checking && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={20}
                  sx={{
                    color:
                      mode === "light"
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                  }}
                />
                <TitleText
                  style={{
                    fontSize: "16px",
                    color: theme.palette.text.primary,
                    margin: 0,
                  }}>
                  Checking for invites to other orgs...
                </TitleText>
                <Info
                  sx={{
                    fontSize: "18px",
                    color: theme.palette.text.secondary,
                    cursor: "help",
                  }}
                  title="If someone has invited you to their organization, we'll automatically add you to it instead of creating a new one."
                />
              </Box>
            </Box>
          )}

          {inviteCheckState?.waiting && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.success.light,
                borderRadius: 1,
                border: `1px solid ${theme.palette.success.main}`,
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress
                  size={20}
                  sx={{ color: theme.palette.success.main }}
                />
                <TitleText
                  style={{
                    fontSize: "16px",
                    color: theme.palette.success.dark,
                    margin: 0,
                  }}>
                  Organization found! Adding you...
                </TitleText>
              </Box>
            </Box>
          )}

          {isCreating ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}>
              <CircularProgress
                sx={{
                  color:
                    mode === "light"
                      ? theme.palette.secondary.main
                      : theme.palette.primary.main,
                }}
              />
              <TitleText
                style={{
                  fontSize: "16px",
                  color: theme.palette.text.primary,
                }}>
                Creating organization and setting up subscription...
              </TitleText>
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                type="submit"
                disabled={
                  isCreating ||
                  inviteCheckState?.checking ||
                  inviteCheckState?.waiting
                }
                data-testid="onboarding-addorg-embed-button-submit">
                Create Organization
              </Button>
              <div style={{ height: "16px" }} />
              <p
                style={{
                  color: theme.palette.text.secondary,
                  fontFamily: "MontserratMed",
                  textAlign: "center",
                  margin: 0,
                }}>
                Or ask the admin of an existing organization to add you.
              </p>
            </>
          )}
        </CenteringDiv>
      </form>

      <Snackbar
        open={autoAssignSnackbar.open}
        autoHideDuration={8000}
        onClose={() => setAutoAssignSnackbar({ ...autoAssignSnackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAutoAssignSnackbar({ ...autoAssignSnackbar, open: false })}
          severity="success"
          sx={{ width: "100%" }}
        >
          {autoAssignSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
